/**
 * Hook for managing login state and authentication logic
 *
 * Extracted from Login.js for testability.
 * Handles Cognito auth, push notifications, and user roles; biometric
 * (Face ID / Touch ID) login lives in useBiometricLogin, and session/role
 * helpers live in util/auth/loginHelpers (re-exported here for
 * backward compatibility).
 */
import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession, signIn, signOut } from 'aws-amplify/auth';

import { isWeb } from '../util/platform';
import Util from '../util/util.js';
import { trackEngagementAsync } from '../util/engagementTracker';
import { removeToken, setLoginMethod } from '../util/storage';
import { useErrorState, showSuccess } from '../util/error';
import {
  isAlreadyAuthenticated,
  storePushNotificationKey,
  getUserRoles,
} from '../util/auth/loginHelpers';

import useBiometricLogin, { CREDS_KEY, BIOMETRICS_ENABLED_KEY } from './useBiometricLogin';
import { t } from '../i18n';

// Re-export for backward compatibility (tests and hooks/index.js import these from here)
export { isAlreadyAuthenticated, storePushNotificationKey, getUserRoles };
export { CREDS_KEY, BIOMETRICS_ENABLED_KEY };

/**
 * Set organization settings in AsyncStorage
 * @deprecated Use Util.setOrganizationSettings directly - this is a re-export for backward compatibility
 */
export async function setOrganizationSettings(organization) {
  return Util.setOrganizationSettings(organization);
}

/**
 * Main login hook
 */
export default function useLogin(navigation, routeParams = {}) {
  const { skipAutoLogin = false, redirectTo = null, redirectParams = null } = routeParams;

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [hidePassword, setHidePassword] = useState(true);
  const [userFontSize, setUserFontSize] = useState(24);

  // Loading state
  const [isBusy, setIsBusy] = useState(false);

  // Error state
  const { error, hasError, handleError, clearError } = useErrorState({
    context: 'Login',
    showToasts: true,
  });

  // Biometrics (Face ID / Touch ID)
  const biometrics = useBiometricLogin(navigation, {
    handleError,
    setEmail,
    setPassword,
    setIsBusy,
  });

  // Auth result state (for navigation)
  const [authResult, setAuthResult] = useState(null);

  /**
   * Toggle password visibility
   */
  const togglePasswordVisibility = useCallback(() => {
    setHidePassword((prev) => !prev);
  }, []);

  /**
   * Execute post-login setup and navigation
   */
  const executeLogin = useCallback(async () => {
    try {
      const { organization, isAdmin, hasUserManagement, hasOrgManagement, isAccountOwner } = await getUserRoles(email);
      await setOrganizationSettings(organization);
      await setLoginMethod('login');

      // Track login for surprise badges (shows toast if badge earned)
      trackEngagementAsync('login', organization);

      // Prompt for biometrics if available and not already set up
      biometrics.maybePromptEnableBiometrics(email, password);

      showSuccess(t('auth:login.success'));

      storePushNotificationKey();

      // Navigate to destination
      if (redirectTo) {
        navigation.navigate(redirectTo, redirectParams || {});
      } else {
        navigation.navigate('SubmitReport', {
          organization,
          admin: isAdmin,
          userAdmin: hasUserManagement,
          orgAdmin: hasOrgManagement,
          accountOwner: isAccountOwner,
        });
      }

      setIsBusy(false);
      setAuthResult({ success: true, organization, isAdmin, hasUserManagement, hasOrgManagement, isAccountOwner });
    } catch (e) {
      if (e.message !== 'Unauthenticated access is not supported for this identity pool.') {
        handleError(e);
      }
      setIsBusy(false);
      setAuthResult({ success: false, error: e.message });
    }
  }, [
    email,
    password,
    biometrics.maybePromptEnableBiometrics,
    navigation,
    redirectTo,
    redirectParams,
    handleError,
  ]);

  /**
   * Attempt biometric login
   */
  const tryBiometricLogin = useCallback(async () => {
    await biometrics.tryBiometricLogin(executeLogin);
  }, [biometrics.tryBiometricLogin, executeLogin]);

  /**
   * Execute sign-in with email/password
   */
  const executeSignIn = useCallback(async () => {
    try {
      await removeToken();
      try {
        await signOut();
      } catch {
        // Ignore signOut errors
      }

      if (await isAlreadyAuthenticated()) return;

      setIsBusy(true);

      const user = await signIn({
        username: email,
        password: password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' },
      });

      if (user.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        navigation.navigate('ResetPassword', {
          initialLogin: true,
          user,
          authenticatedEmail: email,
          oldPassword: password,
        });
      } else {
        await executeLogin();
      }
    } catch (err) {
      setIsBusy(false);
      const message = err.toString();
      if (!message.includes('Pending sign-in')) {
        handleError(err, { userMessage: t('auth:login.error') });
      }
    }
  }, [email, password, navigation, executeLogin, handleError]);

  /**
   * Auto-login on mount if session exists
   */
  const autoLogin = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (session?.tokens?.accessToken?.toString()) {
        setIsBusy(true);
        await executeLogin();
        return;
      }

      // Not signed in - try biometrics if enabled
      if (!isWeb()) {
        await biometrics.attemptAutoBiometricLogin(executeLogin);
      }
    } catch {
      // Ignore auto-login errors
    }
  }, [biometrics.attemptAutoBiometricLogin, executeLogin]);

  // Update font size based on email length
  // baseValue calibrated for V3 UI with icon + padding in input container
  useEffect(() => {
    setUserFontSize(Util.calculateFontSize(email, 36, userFontSize));
  }, [email]);

  // Auto-login on mount
  useEffect(() => {
    if (!skipAutoLogin) {
      autoLogin();
    }
  }, [skipAutoLogin]);

  return {
    // Form state
    email,
    setEmail,
    password,
    setPassword,
    hidePassword,
    togglePasswordVisibility,
    userFontSize,

    // Loading state
    isBusy,

    // Error state
    error,
    hasError,
    clearError,

    // Biometrics state
    biometricAvailable: biometrics.biometricAvailable,
    hasStoredCreds: biometrics.hasStoredCreds,
    bioBusy: biometrics.bioBusy,

    // Auth result
    authResult,

    // Actions
    executeSignIn,
    executeLogin,
    tryBiometricLogin,
    autoLogin,
    saveCredsForBiometrics: biometrics.saveCredsForBiometrics,
  };
}
